import { supabase } from '@/lib/supabase';
import { adminFetch } from '@/services/adminEdgeApi';

// ========== PROPERTIES ==========
export interface Property {
  id: string;
  slug: string;
  code: string;
  title: string;
  type: string;
  purpose: string;
  price: number;
  price_on_request: boolean;
  location: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  garages: number;
  description: string;
  image: string;
  images: string[];
  featured: boolean;
  for_sale: boolean;
  active: boolean;
  created_at: string;
}

export async function getProperties(options?: { includeInactive?: boolean }): Promise<Property[]> {
  console.log('[BACKEND] getProperties - iniciando busca');
  let query = supabase
    .from('properties')
    .select('*')
    .order('id', { ascending: false });
  if (!options?.includeInactive) {
    query = query.eq('active', true);
  }
  const { data, error } = await query;
  if (error) {
    console.error('[BACKEND] getProperties - ERRO do Supabase:', error);
    throw new Error(error.message);
  }
  console.log('[BACKEND] getProperties - retornados', data?.length, 'imóveis');
  return (data || []).map(normalizeProperty);
}

export async function getFeaturedProperties(): Promise<Property[]> {
  console.log('[BACKEND] getFeaturedProperties - iniciando busca');
  const { data, error } = await supabase
    .from('properties')
    .select('*')
    .eq('featured', true)
    .eq('active', true)
    .order('id', { ascending: false });
  if (error) {
    console.error('[BACKEND] getFeaturedProperties - ERRO do Supabase:', error);
    throw new Error(error.message);
  }
  console.log('[BACKEND] getFeaturedProperties - retornados', data?.length, 'imóveis em destaque');
  return (data || []).map(normalizeProperty);
}

export async function getPropertyById(id: string): Promise<Property | null> {
  // Comparing a slug with a UUID id in an OR filter makes Postgres reject
  // the entire query. Select the appropriate column instead.
  const column = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) || /^\d+$/.test(id)
    ? 'id' : 'slug';
  const { data, error } = await supabase
    .from('properties')
    .select('*')
    .eq(column, id)
    .eq('active', true)
    .limit(1);
  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  return row ? normalizeProperty(row) : null;
}

function normalizeProperty(p: any): Property {
  return {
    ...p,
    id: String(p.id),
    slug: p.slug ?? '',
    images: Array.isArray(p.images) ? p.images : (p.image ? [p.image] : []),
    price: p.price ?? 0,
    price_on_request: p.price_on_request ?? false,
    featured: p.featured ?? false,
    for_sale: p.for_sale ?? (p.purpose === 'venda'),
    active: p.active ?? true,
  };
}

/** Gera um slug a partir de um título */
export function generateSlug(title: string): string {
  let slug = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-')
    .slice(0, 80);

  if (!slug || slug.length < 3) {
    slug = 'imovel';
  }

  return slug;
}

/** Gera um slug único, verificando se já existe na tabela properties */
export async function generateUniqueSlug(title: string, excludeId?: string): Promise<string> {
  let base = generateSlug(title);
  if (!base) base = 'imovel';

  let slug = base;
  let suffix = 2;

  while (true) {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id')
        .eq('slug', slug)
        .maybeSingle();

      if (error) {
        console.error('[generateUniqueSlug] Erro na query Supabase:', error.message);
        throw new Error(error.message);
      }

      if (!data || (excludeId && String(data.id) === excludeId)) {
        return slug;
      }

      slug = `${base}-${suffix}`;
      suffix++;

      if (suffix > 100) {
        slug = `${base}-${Date.now().toString(36)}`;
        break;
      }
    } catch (err: any) {
      console.error('[generateUniqueSlug] Falha ao verificar unicidade, usando slug base:', err?.message || err);
      // Fallback: se a query falhar, retorna o slug base sem verificação de unicidade
      return slug;
    }
  }

  return slug;
}

export async function createProperty(data: Omit<Property, 'id' | 'created_at'>): Promise<Property> {
  const ALLOWED_COLUMNS = [
    'slug', 'code', 'title', 'type', 'purpose', 'price', 'price_on_request',
    'location', 'bedrooms', 'bathrooms', 'area', 'garages',
    'description', 'image', 'images', 'featured', 'for_sale', 'active',
  ];

  const cleanPayload: any = {};
  for (const col of ALLOWED_COLUMNS) {
    if ((data as any)[col] !== undefined) {
      cleanPayload[col] = (data as any)[col];
    }
  }

  const json = await adminFetch('POST', 'create-property', cleanPayload);
  return normalizeProperty(json.data);
}

export async function updateProperty(id: string, data: Partial<Property>): Promise<Property> {
  const ALLOWED_COLUMNS = [
    'slug', 'code', 'title', 'type', 'purpose', 'price', 'price_on_request',
    'location', 'bedrooms', 'bathrooms', 'area', 'garages',
    'description', 'image', 'images', 'featured', 'for_sale', 'active',
  ];

  const cleanPayload: any = {};
  for (const col of ALLOWED_COLUMNS) {
    if ((data as any)[col] !== undefined) {
      cleanPayload[col] = (data as any)[col];
    }
  }

  const json = await adminFetch('PUT', 'update-property', { id, data: cleanPayload });
  return normalizeProperty(json.data);
}

export async function deleteProperty(id: string): Promise<void> {
  await adminFetch('DELETE', 'delete-property', { id });
}

// ========== BLOG POSTS ==========
export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  author: string;
  category: string;
  image: string;
  created_at: string;
}

export async function getBlogPosts(): Promise<BlogPost[]> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .order('date', { ascending: false });
  if (error) throw new Error(error.message);
  return (data || []).map((p: any) => ({ ...p, id: String(p.id) }));
}

export async function createBlogPost(data: Omit<BlogPost, 'id' | 'created_at'>): Promise<BlogPost> {
  const json = await adminFetch('POST', 'admin-blog', data);
  return ({ ...json.data, id: String(json.data.id) });
}

export async function updateBlogPost(id: string, data: Partial<BlogPost>): Promise<BlogPost> {
  const json = await adminFetch('PUT', 'admin-blog?id=' + encodeURIComponent(id), data);
  return ({ ...json.data, id: String(json.data.id) });
}

export async function deleteBlogPost(id: string): Promise<void> {
  await adminFetch('DELETE', 'admin-blog?id=' + encodeURIComponent(id));
}

// ========== FAQS ==========
export interface FAQ {
  id: string;
  question: string;
  answer: string;
  order: number;
  created_at: string;
}

export async function getFAQs(): Promise<FAQ[]> {
  const { data, error } = await supabase
    .from('faqs')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw new Error(error.message);
  return (data || []).map((f: any) => ({
    ...f,
    id: String(f.id),
    order: f.sort_order ?? 0,
  }));
}

export async function createFAQ(data: Omit<FAQ, 'id' | 'created_at'>): Promise<FAQ> {
  const json = await adminFetch('POST', 'admin-faqs', { question: data.question, answer: data.answer, sort_order: data.order });
  return { ...json.data, id: String(json.data.id), order: json.data.sort_order ?? 0 };
}

export async function updateFAQ(id: string, data: Partial<FAQ>): Promise<FAQ> {
  const payload: Record<string, unknown> = {};
  if (data.question !== undefined) payload.question = data.question;
  if (data.answer !== undefined) payload.answer = data.answer;
  if (data.order !== undefined) payload.sort_order = data.order;
  const json = await adminFetch('PUT', 'admin-faqs?id=' + encodeURIComponent(id), payload);
  return { ...json.data, id: String(json.data.id), order: json.data.sort_order ?? 0 };
}

export async function deleteFAQ(id: string): Promise<void> {
  await adminFetch('DELETE', 'admin-faqs?id=' + encodeURIComponent(id));
}

// ========== LANÇAMENTOS ==========
export interface Lancamento {
  id: string;
  name: string;
  subtitle: string;
  location: string;
  badge: string;
  description: string;
  stats: { value: string; label: string }[];
  features: { icon: string; text: string }[];
  image: string;
  whatsapp_msg: string;
  active: boolean;
  link: string;
  created_at?: string;
}

export async function getLancamentos(): Promise<Lancamento[]> {
  const { data, error } = await supabase
    .from('lancamentos')
    .select('*')
    .order('id', { ascending: true });
  if (error) throw new Error(error.message);
  return (data || []).map(normalizeLancamento);
}

function normalizeLancamento(l: any): Lancamento {
  return {
    ...l,
    id: String(l.id),
    name: l.name ?? '',
    subtitle: l.subtitle ?? '',
    location: l.location ?? '',
    badge: l.badge ?? 'Lançamento Exclusivo',
    description: l.description ?? '',
    stats: Array.isArray(l.stats) ? l.stats : [],
    features: Array.isArray(l.features) ? l.features : [],
    image: l.image ?? '',
    whatsapp_msg: l.whatsapp_msg ?? '',
    active: l.active ?? true,
    link: l.link ?? '/lancamento',
  };
}

export async function createLancamento(data: Omit<Lancamento, 'id' | 'created_at'>): Promise<Lancamento> {
  const json = await adminFetch('POST', 'admin-lancamentos', data);
  return normalizeLancamento(json.data);
}

export async function updateLancamento(id: string, data: Partial<Lancamento>): Promise<Lancamento> {
  const json = await adminFetch('PUT', 'admin-lancamentos?id=' + encodeURIComponent(id), data);
  return normalizeLancamento(json.data);
}

export async function deleteLancamento(id: string): Promise<void> {
  await adminFetch('DELETE', 'admin-lancamentos?id=' + encodeURIComponent(id));
}

// ========== SITE CONFIG ==========
export interface SiteConfigDB {
  id: string;
  key: string;
  value: string;
  updated_at: string;
}

export async function getSiteConfigAll(): Promise<SiteConfigDB[]> {
  const { data, error } = await supabase.from('site_config').select('*');
  if (error) throw new Error(error.message);
  return (data || []).map((c: any) => ({ ...c, id: String(c.id) }));
}

export async function upsertSiteConfig(key: string, value: string): Promise<void> {
  await adminFetch('POST', 'admin-site-config', { key, value });
}

// ========== ADMIN USERS ==========
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'master' | 'admin';
  profile: 'admin' | 'property_editor';
  is_active: boolean;
  created_at: string;
  last_login: string | null;
  password?: string; // API-only: enviado para Edge Function criar/atualizar auth.users (não é coluna da tabela)
}

export async function getAdminUsers(): Promise<AdminUser[]> {
  const json = await adminFetch('GET', 'admin-users');
  return (json.data || []).map((u: any) => ({ ...u, id: String(u.id), profile: u.profile || 'admin' }));
}

export async function createAdminUser(data: Omit<AdminUser, 'id' | 'created_at' | 'last_login'>): Promise<AdminUser> {
  const json = await adminFetch('POST', 'admin-users', data);
  return { ...json.data, id: String(json.data.id) };
}

export async function updateAdminUser(id: string, data: Partial<AdminUser>): Promise<AdminUser> {
  const json = await adminFetch('PUT', `admin-users?id=${id}`, data);
  return { ...json.data, id: String(json.data.id) };
}

export async function deleteAdminUser(id: string): Promise<void> {
  await adminFetch('DELETE', `admin-users?id=${id}`);
}
